import { ErrorAlert } from "@components/ErrorAlert";
import { queriesRequirementDefinitions } from "@query";
import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSText
} from "@wingspanhq/fe-component-library";
import { RequirementDefinition } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { DefaultActionDetails } from "../components/DefaultActionDetails";

export interface RestoreRequirementModalProps {
  requirementDefinition: RequirementDefinition;
  engagementLength: number;
  onClose: () => void;
}

export const RestoreRequirementModal: React.FC<
  RestoreRequirementModalProps
> = ({ requirementDefinition, onClose, engagementLength }) => {
  const { openSnackbar } = useWSSnackbar();
  const [activateRequirementDefinition, activateRequirementDefinitionMeta] =
    queriesRequirementDefinitions.activateRequirementDefinition.useMutation();

  const handleRestore = async () => {
    await activateRequirementDefinition(
      {
        requirementDefinitionId: requirementDefinition.id!
      },
      {
        onSuccess: () => {
          openSnackbar({
            theme: "dark",
            type: "success",
            message: "Requirement restored"
          });
          onClose();
        }
      }
    );
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to restore this requirement?
      </WSText.ParagraphSm>
      <DefaultActionDetails
        engagementLength={engagementLength}
        requirementDefinition={requirementDefinition}
      />

      <WSAlert
        icon="info-circle"
        my="2XL"
        size="M"
        theme="info"
        title="Restoring requirement - what to expect:"
      >
        Requirement can once again be added to engagements and assigned to
        contractors to complete.
      </WSAlert>

      <ErrorAlert
        context="UpdateRequirement"
        error={activateRequirementDefinitionMeta.error}
        mb="XL"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Restore requirement",
            loading: activateRequirementDefinitionMeta.isLoading,
            onAsyncClick: handleRestore,
            kind: "Primary"
          }
        ]}
      />
    </>
  );
};

export const useRestoreRequirementModal = () => {
  return useWSModal(RestoreRequirementModal, {
    title: "Restore requirement",
    size: "M"
  });
};
